import {useCallback, useState, MutableRefObject} from 'react';
import {Platform} from 'react-native';
import {store, useAppDispatch} from '../../../redux';
import {blockReissueIfAlreadySubmitted} from '../../../redux/actions/cardReissue';
import {getEsiaLink} from '../../../redux/actions/login';
import {getHolderProfile} from '../../../redux/actions/profile';
import {holderProfileSelector} from '../../../redux/selectors/profile';
import {AuthorizeEsiaResponsePayload} from '../../../shared/models/api/authorizeEsiaResponsePayload';
import {GetHolderProfileCabinetResponsePayload} from '../../../shared/models/api';
import {Screens} from '../../../shared/routing';
import {startCardReissueEsiaPending} from '../../../shared/storage/cardReissueEsia';

type Params = {
  isDesktop: boolean;
  isAuthorized: boolean;
  esiaRegInfo: AuthorizeEsiaResponsePayload | null;
  holderProfile: GetHolderProfileCabinetResponsePayload | null;
  hideAndLockGlobalLoader: () => void;
  cancelEsia: () => Promise<void>;
  unlockGlobalLoader: () => void;
  showGlobalLoader: () => void;
  navigation: {
    navigate: (...args: any[]) => void;
  };
  handledEsiaTokenRef: MutableRefObject<string | null>;
};

export const useReissueEsia = ({
  isDesktop,
  isAuthorized,
  esiaRegInfo,
  holderProfile,
  hideAndLockGlobalLoader,
  cancelEsia,
  unlockGlobalLoader,
  showGlobalLoader,
  navigation,
  handledEsiaTokenRef,
}: Params) => {
  const dispatch = useAppDispatch();
  const [submitting, setSubmitting] = useState(false);

  const onEsiaClick = useCallback(async () => {
    if (await dispatch(blockReissueIfAlreadySubmitted())) {
      return;
    }

    setSubmitting(true);
    unlockGlobalLoader();
    showGlobalLoader();
    try {
      let holderSnils = holderProfile?.snils ?? store.getState().profile.holder?.snils;
      if (!holderSnils) {
        await dispatch(getHolderProfile());
        holderSnils = holderProfileSelector()(store.getState())?.snils;
      }
      await startCardReissueEsiaPending(holderSnils);

      // Native mobile: ESIA in WebView. Adaptive + desktop web: getEsiaLink (same path).
      if (isAuthorized && !isDesktop && Platform.OS !== 'web') {
        if (esiaRegInfo?.token) {
          handledEsiaTokenRef.current = esiaRegInfo.token;
        }
        hideAndLockGlobalLoader();
        (navigation.navigate as (screen: string, params?: object) => void)(Screens.Profile, {
          screen: Screens.EsiaLoginAfterLogged,
          params: {fromReissue: true, esiaSession: Date.now()},
        });
        return;
      }
      await dispatch(getEsiaLink({showError: false, fromReissue: true}));
    } catch {
      await cancelEsia();
    } finally {
      setSubmitting(false);
    }
  }, [
    cancelEsia,
    dispatch,
    esiaRegInfo,
    handledEsiaTokenRef,
    hideAndLockGlobalLoader,
    holderProfile?.snils,
    isAuthorized,
    isDesktop,
    navigation,
    unlockGlobalLoader,
    showGlobalLoader,
  ]);

  return {submitting, setSubmitting, onEsiaClick};
};
